import type { ModelRow, ProviderView } from "./api";

export interface ModelView extends ModelRow {
  /** Joined provider view (health, latency, branding) when available. */
  providerView?: ProviderView;
}

const CAP_LABEL: Record<string, string> = {
  chat: "Chat",
  streaming: "Streaming",
  toolCalling: "Tools",
  tool_calling: "Tools",
  vision: "Vision",
  image_understanding: "Vision",
  embeddings: "Embeddings",
  structuredOutput: "JSON",
  structured_output: "JSON",
  model_listing: "Discovery",
  imageGeneration: "Images",
  audioInput: "Audio in",
  audioOutput: "Audio out",
  jsonMode: "JSON mode",
};

export function capabilityChips(caps: Record<string, boolean>): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(caps)) {
    if (v && CAP_LABEL[k] && !out.includes(CAP_LABEL[k])) out.push(CAP_LABEL[k]);
  }
  return out;
}

export function formatContext(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`;
  return String(tokens);
}

/**
 * Sanitize per-million pricing: catalogs may carry sentinel placeholders
 * (negative values like -1000000), non-finite numbers, absurd magnitudes,
 * or float artifacts (0.30000000000000004). Returns null when no honest
 * price can be shown.
 */
function sanePrice(v: number | null | undefined): number | null {
  if (v == null || !Number.isFinite(v) || v < 0 || v > 10_000) return null;
  return Number(v.toFixed(4));
}

export function formatPrice(m: ModelRow): string {
  if (m.pricing.tier === "free") return "Free";
  const inp = sanePrice(m.pricing.inputPerMillion);
  const out = sanePrice(m.pricing.outputPerMillion);
  if (inp == null && out == null)
    return m.pricing.tier === "unknown" ? "—" : "Paid";
  if (inp != null && out != null) return `$${inp}/$${out} per M`;
  return `$${inp ?? out} per M`;
}

/** Short tier label for chips — unknown pricing is shown honestly. */
export function tierLabel(m: ModelRow): "FREE" | "PAID" | "N/A" {
  if (m.pricing.tier === "free") return "FREE";
  if (m.pricing.tier === "unknown") return "N/A";
  return "PAID";
}

export type ModelFilterId =
  | "all"
  | "free"
  | "paid"
  | "coding"
  | "vision"
  | "tools"
  | "embeddings"
  | "fast"
  | "longctx"
  | "demo";

/** Demo providers (`mock-*`) are test fixtures, not production capacity. */
export function isDemoModel(m: ModelView): boolean {
  const typeId = m.providerView?.providerTypeId ?? m.provider?.providerTypeId ?? "";
  return typeId.startsWith("mock-");
}

export function modelMatchesFilter(m: ModelView, f: ModelFilterId): boolean {
  switch (f) {
    case "demo":
      return isDemoModel(m);
    case "free":
      return m.pricing.tier === "free";
    case "paid":
      return m.pricing.tier !== "free";
    case "coding":
      return (m.scores.coding ?? 0) >= 0.7;
    case "vision":
      return Boolean(m.capabilities.vision);
    case "tools":
      return Boolean(m.capabilities.toolCalling);
    case "embeddings":
      return Boolean(m.capabilities.embeddings);
    case "fast":
      return (m.scores.latency ?? 0) >= 0.7;
    case "longctx":
      return m.contextWindow >= 100_000;
    default:
      return true;
  }
}

export function modelMatchesQuery(m: ModelView, q: string): boolean {
  const query = q.trim().toLowerCase();
  if (!query) return true;
  const blob = [
    m.displayName,
    m.providerModelId,
    m.logicalId,
    m.provider?.displayName ?? "",
    m.provider?.providerTypeId ?? "",
    m.pricing.tier === "free" ? "free" : "paid",
    ...capabilityChips(m.capabilities),
  ]
    .join(" ")
    .toLowerCase();
  return query.split(/\s+/).every((t) => blob.includes(t));
}

export type ModelSort = "recommended" | "fastest" | "quality" | "context" | "cost";

/** Weighted recommendation: quality + reliability + free bonus + latency. */
export function recommendedScore(m: ModelView): number {
  const s = m.scores;
  let score =
    (s.quality ?? 0.5) * 0.35 +
    (s.reliability ?? 0.5) * 0.25 +
    (s.latency ?? 0.5) * 0.15 +
    (s.coding ?? 0.5) * 0.15;
  if (m.pricing.tier === "free") score += 0.15;
  if (m.providerView?.connectionStatus === "healthy") score += 0.1;
  else if (m.providerView && m.providerView.connectionStatus !== "connected")
    score -= 0.1;
  return score;
}

/** Sort key for cost — sentinel/invalid prices sort as most expensive. */
function costKey(m: ModelView): number {
  const i = sanePrice(m.pricing.inputPerMillion);
  const o = sanePrice(m.pricing.outputPerMillion);
  if (i == null && o == null) return Number.MAX_SAFE_INTEGER;
  return (i ?? 0) + (o ?? 0);
}

export function sortModels(list: ModelView[], sort: ModelSort): ModelView[] {
  const copy = [...list];
  switch (sort) {
    case "fastest":
      return copy.sort(
        (a, b) =>
          (b.scores.latency ?? 0) - (a.scores.latency ?? 0) ||
          (a.providerView?.health?.p50LatencyMs ?? 9e9) -
            (b.providerView?.health?.p50LatencyMs ?? 9e9),
      );
    case "quality":
      return copy.sort((a, b) => (b.scores.quality ?? 0) - (a.scores.quality ?? 0));
    case "context":
      return copy.sort((a, b) => b.contextWindow - a.contextWindow);
    case "cost":
      return copy.sort((a, b) => costKey(a) - costKey(b));
    default:
      return copy.sort((a, b) => recommendedScore(b) - recommendedScore(a));
  }
}
